import { DollarFormatter, EmployeeFormatter } from "./formatting";

// Slider extremes. Sending a bound that sits on an extreme is equivalent to
// "no filter", which is why searchQuery.js collapses those values to null.
export const REVENUE_MIN = 1_000_000;
export const REVENUE_MAX = 999_000_000;
export const EMPLOYEE_MIN = 0;
export const EMPLOYEE_MAX = 500_000;

// Absorbs floating-point noise from the log/exp slider math without swallowing
// legitimate near-edge values. Mirrors the epsilon used when building queries.
const BOUND_EPSILON = 0.01;

const NUMERIC_INPUT_PATTERN = /^(\d+\.?\d*)\s*([KkMmBb]?)$/;
const SUFFIX_MULTIPLIERS = { K: 1_000, M: 1_000_000, B: 1_000_000_000 };

const parseAmount = (raw, defaultMultiplier) => {
  const normalized = String(raw).trim().replace(/[,$\s]/g, "");
  const match = normalized.match(NUMERIC_INPUT_PATTERN);
  if (!match) return NaN;
  const suffix = match[2].toUpperCase();
  return parseFloat(match[1]) * (SUFFIX_MULTIPLIERS[suffix] ?? defaultMultiplier);
};

export const parseRawNumber = (raw) => parseAmount(raw, 1);

// Revenue is typed in millions unless the user adds an explicit K/M/B suffix.
export const parseRevenueMillions = (raw) => parseAmount(raw, 1_000_000);

export const clampToRange = (value, min, max) => Math.max(min, Math.min(max, value));

export const isAtExtreme = (value, extreme) =>
  value == null || Math.abs(value - extreme) <= BOUND_EPSILON;

// Revenue steps by a tenth of its current order of magnitude so a single click
// is always visible, while headcount steps one employee at a time.
const revenueStep = (current, direction) => {
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(1, current))));
  return current + direction * Math.max(1_000, magnitude / 10);
};

export const REVENUE_SCALE = {
  min: REVENUE_MIN,
  max: REVENUE_MAX,
  toSlider: (value) => Math.log(value),
  fromSlider: (slider) => Math.exp(slider),
  parse: parseRevenueMillions,
  format: (value) => DollarFormatter.format(value),
  step: revenueStep,
  minPlaceholder: "Min (M)",
  maxPlaceholder: "Max (M)",
};

export const EMPLOYEE_SCALE = {
  min: EMPLOYEE_MIN,
  max: EMPLOYEE_MAX,
  toSlider: (value) => Math.log(1 + value),
  fromSlider: (slider) => Math.exp(slider) - 1,
  parse: parseRawNumber,
  format: (value) => EmployeeFormatter.format(value),
  step: (current, direction) => current + direction,
  minPlaceholder: "Min",
  maxPlaceholder: "Max",
};

/**
 * Map the committed query bounds onto slider positions, falling back to the
 * scale extremes so an unset filter opens fully expanded rather than empty.
 */
export const boundsToSlider = (lowerBound, upperBound, scale) => [
  scale.toSlider(lowerBound ?? scale.min),
  scale.toSlider(upperBound ?? scale.max),
];

/**
 * Move one thumb and push the opposite thumb along when the two would cross.
 */
export const withThumbAt = (sliderValues, index, sliderValue) => {
  const next = [...sliderValues];
  next[index] = sliderValue;
  if (index === 0 && next[0] > next[1]) next[1] = next[0];
  if (index === 1 && next[1] < next[0]) next[0] = next[1];
  return next;
};
